/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Spin, Button, Input } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadNonFtPremiumBookingsReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";

const NonFtPremiumBookingsReport = () => {
    // Set default date range to yesterday and today
    const defaultDateRange = [
        moment().subtract(1, "days"),
        moment()
    ];

    const [loading, setLoading] = useState(false);
    const [hubListLoading, setHubListLoading] = useState(false);
    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [hubList, setHubList] = useState([]);

    // Filters
    const [originHubIds, setOriginHubIds] = useState([]);
    const [isOriginHubDisabled, setIsOriginHubDisabled] = useState(false);
    const [docTypes, setDocTypes] = useState([]);
    const [travelTypes, setTravelTypes] = useState([]);
    const [bookingTypes, setBookingTypes] = useState([]);
    const [awbNumbers, setAwbNumbers] = useState("");

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setHubListLoading(true);
                const hubs = await getHubList();

                // Clean up hub labels if they contain the ID (e.g. "123 - Hub Name" -> "Hub Name")
                const cleanedHubs = hubs.map(hub => {
                    const id = String(hub.value);
                    const label = String(hub.label);
                    // Check if label starts with ID, followed by optional separator
                    if (label.startsWith(id)) {
                        // Remove ID and any following separator (space, hyphen, colon)
                        const newLabel = label.replace(new RegExp(`^${id}\\s*[-:]?\\s*`), "");
                        return { ...hub, label: newLabel || label };
                    }
                    return hub;
                });

                // Get user info and active hub for auto-selection
                const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
                const userid = JSON.parse(localStorage.getItem("userId") || "{}");
                const activeHub = JSON.parse(localStorage.getItem("active_hub") || "null");
                
                const role = userinfo?.roles?.[0];
                const isHubRestricted = role === "ROLE_HUBADMIN" || role === "ROLE_HUBOPERATOR";
                
                const targetHubId = activeHub?.hubId || userid?.userInfo?.premiseId;

                if (targetHubId) {
                    const hubIdStr = String(targetHubId);
                    const matchingHub = cleanedHubs.find(h => String(h.value) === hubIdStr);
                    
                    if (matchingHub) {
                        setOriginHubIds([matchingHub.value]);
                        if (isHubRestricted) setIsOriginHubDisabled(true);
                    } else if (activeHub?.hubName) {
                        // Fallback: manually add the logged hub if it's not in the fetched list
                        const manualHub = { label: activeHub.hubName, value: targetHubId };
                        cleanedHubs.push(manualHub);
                        setOriginHubIds([targetHubId]);
                        if (isHubRestricted) setIsOriginHubDisabled(true);
                    }
                } else if (isHubRestricted) {
                    // Safety: if restricted but no hub ID found, keep initialization flag
                    setIsOriginHubDisabled(true);
                }

                setHubList(cleanedHubs);
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                setHubListLoading(false);
                setIsInitialized(true);
            }
        })();
    }, []);

    // Fetch report data when filters or pagination change
    useEffect(() => {
        // Only fetch if date range is present and component is initialized
        if (!isInitialized) return;

        if (dateRange && dateRange[0] && dateRange[1]) {
            fetchReport();
        }
        // eslint-disable-next-line
    }, [dateRange, originHubIds, docTypes, travelTypes, bookingTypes, awbNumbers, pagination.current, pagination.pageSize, isInitialized]);


    const fetchReport = async () => {
        // Create new request with cancellation support
        const { signal, requestId } = createNewRequest("non-ft-premium-bookings-report");

        setLoading(true);
        try {
            const processedAwbNumbers = awbNumbers ? awbNumbers.split(",").map(val => val.trim()).filter(Boolean) : undefined;
            // Prepare params according to API requirements
            const params = {
                ...(originHubIds?.length > 0 && { originHubIds: originHubIds.join(",") }),
                ...(docTypes?.length > 0 && { docTypes: docTypes.join(",") }),
                ...(travelTypes?.length > 0 && { travelTypes: travelTypes.join(",") }),
                ...(bookingTypes?.length > 0 && { bookingTypes: bookingTypes.join(",") }),
                ...(processedAwbNumbers && { awbNumbers: processedAwbNumbers.join(",") }),
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                page: pagination.current - 1,
                size: pagination.pageSize
            };

            const reportResponse = await ReportsService.getNonFtPremiumBookingsReport(params, { signal });
            const { data, total, lastRefreshedAt, nextScheduledRefresh: nextRefresh } = reportResponse;

            // Check if this is still the latest request
            if (!isLatestRequest(requestId)) {
                return;
            }

            setReportData(data);
            setLastsynced(lastRefreshedAt);
            setNextScheduledRefresh(nextRefresh);
            setPagination(prev => ({
                ...prev,
                total,
            }));
        } catch (error) {
            if (!isLatestRequest(requestId)) return;
            // Ignore cancellation errors
            if (error.name === "AbortError" || error.name === "CanceledError" || error.code === "ERR_CANCELED") {
                return;
            }

            console.error("Error fetching report data:", error);
            // Show error to user if it's not a cancellation
            message.error(error.message || "Failed to fetch report data");

            setReportData([]);
            setPagination(prev => ({ ...prev, total: 0 }));
        } finally {
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };


    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        try {
            if (!dateRange || !dateRange[0] || !dateRange[1]) {
                message.error("Please select date range for download");
                return;
            }

            const processedAwbNumbers = awbNumbers ? awbNumbers.split(",").map(val => val.trim()).filter(Boolean) : undefined;

            const downloadParams = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                originHubIds: originHubIds.length > 0 ? originHubIds.join(",") : undefined,
                docTypes: docTypes.length > 0 ? docTypes.join(",") : undefined,
                travelTypes: travelTypes.length > 0 ? travelTypes.join(",") : undefined,
                bookingTypes: bookingTypes.length > 0 ? bookingTypes.join(",") : undefined,
                awbNumbers: processedAwbNumbers ? processedAwbNumbers.join(",") : undefined
            };

            await downloadNonFtPremiumBookingsReport(downloadParams);
            message.success("Download started successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to start download");
        }
    };

    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 70,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "AWB Number",
            dataIndex: "awbNumber",
            key: "awbNumber",
            width: 150,
            render: (text) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        { title: "Booking Date & Time", dataIndex: "bookingDatetime", key: "bookingDatetime", width: 180, render: (text) => text || "---" },
        { title: "Content", dataIndex: "content", key: "content", width: 200, render: (text) => text || "---" },
        { title: "Doc Type", dataIndex: "docType", key: "docType", width: 100, render: (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : "---" },
        { title: "Travel Type", dataIndex: "travelType", key: "travelType", width: 120, render: (text) => text ? text.toUpperCase() : "---" },
        { title: "Booking Type", dataIndex: "bookingType", key: "bookingType", width: 120, render: (text) => text || "---" },
        { title: "Origin Pincode", dataIndex: "originPincode", key: "originPincode", width: 120, render: (text) => text || "---" },
        { title: "Destination Pincode", dataIndex: "destinationPincode", key: "destinationPincode", width: 150, render: (text) => text || "---" },
        { title: "Origin Hub", dataIndex: "originHub", key: "originHub", width: 180, render: (text) => text || "---" },
        { title: "Origin CP", dataIndex: "originChannelPartner", key: "originChannelPartner", width: 180, render: (text) => text || "---" },
        { title: "Origin CP City", dataIndex: "originCpCity", key: "originCpCity", width: 150, render: (text) => text || "---" },
        { title: "Origin CP State", dataIndex: "originCpState", key: "originCpState", width: 150, render: (text) => text || "---" },
    ];

    const docTypeOptions = [
        { label: "DOX", value: "dox" },
        { label: "NON-DOX", value: "non-dox" },
    ];

    const travelTypeOptions = [
        { label: "Surface", value: "surface" },
        { label: "Air", value: "air" },
    ];

    const bookingTypeOptions = [
        { label: "COURIER", value: "COURIER" },
        { label: "CARGO", value: "CARGO" },
        { label: "INTERNATIONAL", value: "INTERNATIONAL" },
        { label: "ACCOUNT", value: "ACCOUNT" },
        { label: "E-COMM", value: "E-COMM" },
        { label: "CASH", value: "CASH" },
        { label: "LTL", value: "LTL" },
        { label: "FTL", value: "FTL" },

    ];

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Non-FT Premium Bookings Report">
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking Date Range</div>
                        <CalendarComponent
                            type="range"
                            value={dateRange}
                            onChange={(val) => {
                                setDateRange(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Origin Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Origin Hub"
                            options={hubList}
                            onChange={(val) => {
                                // Handle both single value (when disabled/default mode) and array (multiple mode)
                                setOriginHubIds(Array.isArray(val) ? val : (val ? [val] : []));
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={isOriginHubDisabled ? (originHubIds[0] || null) : originHubIds}
                            allowClear
                            mode={isOriginHubDisabled ? "default" : "multiple"}
                            loading={hubListLoading}
                            showSearch={true}
                            optionFilterProp="label"
                            disabled={isOriginHubDisabled}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Doc Type</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Doc Type"
                            options={docTypeOptions}
                            onChange={(val) => {
                                setDocTypes(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={docTypes}
                            allowClear
                            mode="multiple"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Travel Type</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Travel Type"
                            options={travelTypeOptions}
                            onChange={(val) => {
                                setTravelTypes(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={travelTypes}
                            allowClear
                            mode="multiple"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking Type</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Booking Type"
                            options={bookingTypeOptions}
                            onChange={(val) => {
                                setBookingTypes(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={bookingTypes}
                            allowClear
                            mode="multiple"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">AWB Numbers</div>
                        <Input
                            className="custom-input"
                            placeholder="Search AWB Numbers (comma separated)"
                            value={awbNumbers}
                            onChange={(e) => {
                                setAwbNumbers(e.target.value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">&nbsp;</div>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={handleDownload}
                            disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                            style={{ width: "100%" }}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
            </Card>
            <Spin spinning={loading}>
                <TableComponent
                    columns={columns}
                    dataSource={reportData}
                    loading={false}
                    pagination={pagination}
                    onChange={handleTableChange}
                    scroll={{ y: "calc(100vh - 350px)", x: "max-content" }}
                    sticky
                />
            </Spin>
        </div>
    );
};

export default NonFtPremiumBookingsReport;
